// Ghost admin-selectable custom template's dedicated JS entry — only
// custom-truchet-generator.hbs loads this bundle (assets/built/truchet-generator.js).
import { buildSvg, isValidState, normalizeState, randomState, sanitizeState } from './truchet-lib.js';
import { tiles } from './truchet-tiles-data.js';
import { buildPrintsEndpoint, buildPrintMap, getAvailabilityView, renderAvailabilityHtml } from './print-availability.js';

const REVEAL_INTERVAL_MS = 80;
const COPY_FEEDBACK_MS = 1500;

function fetchPrintMap(supabaseUrl, supabaseAnonKey) {
    if (!supabaseUrl || !supabaseAnonKey) {
        console.error('[truchet-generator] @custom.supabase_url/supabase_anon_key are not set');
        return Promise.resolve(new Map());
    }
    return fetch(buildPrintsEndpoint(supabaseUrl), {
        headers: {
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${supabaseAnonKey}`,
        },
    })
        .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        })
        .then(buildPrintMap)
        .catch((err) => {
            console.error('[truchet-generator] print availability fetch failed:', err);
            return new Map();
        });
}

function readCodeFromQuery() {
    const raw = new URLSearchParams(window.location.search).get('code');
    if (!raw) return null;
    const normalized = normalizeState(raw);
    return isValidState(normalized) ? normalized : null;
}

function initTruchetGenerator() {
    const root = document.querySelector('.truchet-gen');
    if (!root) return;

    const canvas = root.querySelector('.truchet-gen__canvas');
    const input = root.querySelector('.truchet-gen__input');
    const randomizeButton = root.querySelector('.truchet-gen__randomize');
    const copyLinkButton = root.querySelector('.truchet-gen__copy-link');
    const availabilityRegion = root.querySelector('.truchet-gen__availability');

    let revealTimer = null;
    let copyFeedbackTimer = null;
    let printMap = new Map();
    let currentCode = '';

    function render(code) {
        canvas.innerHTML = buildSvg(code, tiles);
    }

    function updateAvailability(code) {
        if (!availabilityRegion) return;
        if (!isValidState(code)) {
            availabilityRegion.hidden = true;
            availabilityRegion.innerHTML = '';
            return;
        }
        const view = getAvailabilityView(printMap, code);
        availabilityRegion.hidden = false;
        availabilityRegion.innerHTML = renderAvailabilityHtml(view, code);
    }

    function renderAll(code) {
        currentCode = code;
        render(code);
        syncCopyLinkAvailability(code);
        updateAvailability(code);
    }

    function syncCopyLinkAvailability(code) {
        copyLinkButton.disabled = !isValidState(code);
    }

    function clearReveal() {
        if (revealTimer !== null) {
            window.clearInterval(revealTimer);
            revealTimer = null;
        }
    }

    function revealTarget(target) {
        clearReveal();
        input.value = '';
        renderAll('');
        let i = 0;
        revealTimer = window.setInterval(() => {
            i += 1;
            const code = target.slice(0, i);
            input.value = code;
            renderAll(code);
            if (i >= target.length) {
                clearReveal();
            }
        }, REVEAL_INTERVAL_MS);
    }

    input.addEventListener('input', () => {
        clearReveal();
        const sanitized = sanitizeState(input.value);
        input.value = sanitized;
        renderAll(sanitized);
    });

    randomizeButton.addEventListener('click', () => {
        revealTarget(randomState());
    });

    copyLinkButton.addEventListener('click', () => {
        const url = `${window.location.origin}${window.location.pathname}?code=${input.value}`;
        navigator.clipboard.writeText(url).then(() => {
            if (copyFeedbackTimer !== null) {
                window.clearTimeout(copyFeedbackTimer);
            }
            const originalLabel = 'Copy Link';
            copyLinkButton.textContent = 'Copied!';
            copyFeedbackTimer = window.setTimeout(() => {
                copyLinkButton.textContent = originalLabel;
                copyFeedbackTimer = null;
            }, COPY_FEEDBACK_MS);
        });
    });

    revealTarget(readCodeFromQuery() ?? randomState());

    fetchPrintMap(root.dataset.supabaseUrl, root.dataset.supabaseAnonKey).then((map) => {
        printMap = map;
        updateAvailability(currentCode);
    });
}

initTruchetGenerator();
